import { observer } from "mobx-react-lite";
import ResizeTextarea, { TextareaAutosizeProps } from "react-textarea-autosize";
import { Center, forwardRef, Textarea, TextareaProps } from "@chakra-ui/react";

import { Lang } from "@noli/core";
import { translate } from "@noli/business";

import { themeStore } from "@/stores/theme";
import { IWithFolding } from "@/components/SubPanel/WithFolding";

type AutoResizeTextareaProps = TextareaAutosizeProps &
  TextareaProps & {
    lang: Lang;
    placeholderWord: string;
  };
const AutoResizeTextarea = forwardRef<AutoResizeTextareaProps, "textarea">(
  ({ lang, placeholderWord, ...others }, ref) => {
    return (
      <Textarea
        overflow="hidden"
        w="100%"
        resize="none"
        ref={ref}
        minRows={1}
        placeholder={translate(placeholderWord, lang)}
        _placeholder={{
          color: "#6a6a6a",
        }}
        fontSize="14px"
        as={ResizeTextarea}
        {...others}
      />
    );
  },
);

export type TextAreaProps = {
  w: IWithFolding["w"];
  lang: Lang;
  textAreaH: number;
  textAreaExpandH?: number;
  placeholderWord: string;
  text: string;
  setText: (text: string) => void;
} & AutoResizeTextareaProps;
function TextArea({
  w,
  lang,
  textAreaH,
  textAreaExpandH,
  placeholderWord,
  text,
  setText,
  ...others
}: TextAreaProps) {
  const { inputColor, borderColor, scrollBarColor } = themeStore.styles;

  return (
    <Center>
      <AutoResizeTextarea
        w={w}
        lang={lang}
        placeholderWord={placeholderWord}
        minH={others.minRows ? "none" : `${textAreaH}px`}
        maxH={others.maxRows ? "none" : `${textAreaH + (textAreaExpandH ?? 0)}px`}
        mt="16px"
        color={inputColor}
        overflow="auto"
        border="1px"
        borderColor={borderColor}
        borderRadius="6px"
        style={{ caretColor: inputColor }}
        value={text}
        onChange={(e) => setText(e.target.value)}
        _hover={{
          borderColor: "#3fc9a8",
        }}
        _active={{
          borderColor: "#36c19e",
        }}
        _focus={{
          borderColor: "#36c19e",
        }}
        css={{
          "&::-webkit-scrollbar": {
            width: "8px",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            color: "transparent",
            boxShadow: "inset 0 0 0 10px",
            transition: "color .3s ease",
          },
          "&:hover::-webkit-scrollbar-thumb": {
            color: scrollBarColor,
          },
        }}
        {...others}
      />
    </Center>
  );
}

export default observer(TextArea);
